"use client"

import { format } from "date-fns"
import { CalendarIcon, X } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/v2/button"
import { Calendar } from "@/components/ui/v2/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/v2/popover"
import { cn } from "@/lib/utils/cn"

interface DatePickerProps {
  value?: Date
  onChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
  className?: string
  allowClear?: boolean
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
  minDate,
  maxDate,
  className,
  allowClear = false,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)

  const handleSelect = (date: Date | undefined) => {
    if (!date && !allowClear) return
    onChange?.(date)
    setOpen(false)
  }

  const handleClear = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onChange?.(undefined)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative w-full">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal pr-8",
              !value && "text-muted-foreground",
              className
            )}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
            <span className="flex-1 text-sm text-left min-w-0">
              {value ? (
                <>
                  <span className="hidden sm:inline">{format(value, "MMM dd, yyyy")}</span>
                  <span className="sm:hidden">{format(value, "MM/dd/yy")}</span>
                </>
              ) : (
                <>
                  <span className="hidden sm:inline">{placeholder}</span>
                  <span className="sm:hidden">{placeholder}</span>
                </>
              )}
            </span>
          </Button>
        </PopoverTrigger>
        {allowClear && value && (
          <button
            type="button"
            onClick={handleClear}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 flex-shrink-0 opacity-50 hover:opacity-100 cursor-pointer flex items-center justify-center bg-background rounded-sm border-0 p-0 z-10"
            aria-label="Clear date"
            tabIndex={disabled ? -1 : 0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleClear(e);
              }
            }}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={handleSelect}
          disabled={(date) => {
            if (disabled) return true
            if (minDate && date < minDate) return true
            if (maxDate && date > maxDate) return true
            return false
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}
